"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;
var _propTypes = _interopRequireDefault(require("prop-types"));
var _react = _interopRequireDefault(require("react"));
var _Platform = _interopRequireDefault(require("react-native-web/dist/cjs/exports/Platform"));
var _View = _interopRequireDefault(require("react-native-web/dist/cjs/exports/View"));
var _ThemeProvider = require("../ThemeProvider");
var _utils = require("../utils");
var _ViewportProvider = require("../ViewportProvider");
var _jsxRuntime = require("react/jsx-runtime");
function _interopRequireDefault(e) { return e && e.__esModule ? e : { default: e }; }
const selectDotStyles = _ref => {
  let {
    dotWidth,
    timelineColor,
    dotBorderWidth,
    dotColor
  } = _ref;
  return {
    width: dotWidth,
    height: dotWidth,
    borderRadius: dotWidth / 2,
    backgroundColor: dotColor,
    borderWidth: dotBorderWidth,
    borderColor: timelineColor
  };
};
const selectConnectorStyles = _ref2 => {
  let {
    timelineConnectorColor,
    connectorHeight,
    connectorWidth
  } = _ref2;
  return {
    width: connectorWidth,
    height: connectorHeight,
    backgroundColor: timelineConnectorColor
  };
};
const selectTimelineContainerStyle = _ref3 => {
  let {
    timelineContainerDirection
  } = _ref3;
  return {
    flexDirection: timelineContainerDirection
  };
};
const selectLineItemStyles = _ref4 => {
  let {
    lineItemAlign,
    lineItemDirection,
    lineItemMarginBottom,
    lineItemMarginRight
  } = _ref4;
  return {
    alignItems: lineItemAlign,
    flexDirection: lineItemDirection,
    marginBottom: lineItemMarginBottom,
    marginRight: lineItemMarginRight,
    overflow: 'hidden'
  };
};
const selectLineItemContainer = _ref5 => {
  let {
    lineItemContainerDirection,
    lineContainerFlexSize
  } = _ref5;
  return {
    flexDirection: lineItemContainerDirection,
    flex: lineContainerFlexSize
  };
};
const selectItemContentStyles = (_ref6, isLastChild) => {
  let {
    itemContentFlexSize,
    itemContentMarginBottom,
    itemContentMarginRight
  } = _ref6;
  return {
    flex: itemContentFlexSize,
    marginBottom: !isLastChild && itemContentMarginBottom,
    marginRight: !isLastChild && itemContentMarginRight
  };
};
const [selectProps, selectedSystemPropTypes] = (0, _utils.selectSystemProps)([_utils.a11yProps, _utils.viewProps]);

/**
 * Timeline is a component that displays either a horizontal or vertical list of the
 * children components passed by props
 *
 * ## Component API
 *
 * - `horizontal` In order to display the Component list horizontally
 *
 *
 * ## A11y guidelines
 * Timeline link supports all the common a11y props.
 */
const Timeline = /*#__PURE__*/_react.default.forwardRef((_ref7, ref) => {
  let {
    tokens,
    variant = {},
    children,
    accessibilityLabel,
    tag = 'ul',
    childrenTag = 'li',
    ...rest
  } = _ref7;
  const viewport = (0, _ViewportProvider.useViewport)();
  const themeTokens = (0, _ThemeProvider.useThemeTokens)('Timeline', tokens, variant, {
    viewport
  });
  const containerProps = {
    ...selectProps(rest),
    ...(0, _utils.getA11yPropsFromHtmlTag)(tag, rest.accessibilityRole || 'list'),
    accessibilityLabel
  };
  return /*#__PURE__*/(0, _jsxRuntime.jsx)(_View.default, {
    ...containerProps,
    ref: ref,
    style: selectTimelineContainerStyle(themeTokens),
    children: children.map((child, index) => {
      const accessibilityRole = _Platform.default.OS !== 'android' ? 'listitem' : undefined;
      const childrenProps = {
        ...(0, _utils.getA11yPropsFromHtmlTag)(childrenTag, child?.props?.accessibilityRole || accessibilityRole)
      };
      return /*#__PURE__*/(0, _jsxRuntime.jsxs)(_View.default, {
        style: selectLineItemContainer(themeTokens)
        // eslint-disable-next-line react/no-array-index-key
        ,
        ...childrenProps,
        children: [/*#__PURE__*/(0, _jsxRuntime.jsxs)(_View.default, {
          style: selectLineItemStyles(themeTokens),
          children: [/*#__PURE__*/(0, _jsxRuntime.jsx)(_View.default, {
            style: selectDotStyles(themeTokens)
          }), /*#__PURE__*/(0, _jsxRuntime.jsx)(_View.default, {
            style: selectConnectorStyles(themeTokens)
          })]
        }), /*#__PURE__*/(0, _jsxRuntime.jsx)(_View.default, {
          style: selectItemContentStyles(themeTokens, index + 1 === children.length),
          children: child
        })]
      }, `timeline-${index}-${child.displayName}`);
    })
  });
});
Timeline.displayName = 'Timeline';
Timeline.propTypes = {
  ...selectedSystemPropTypes,
  tokens: (0, _utils.getTokensPropType)('Timeline'),
  variant: _utils.variantProp.propType,
  /**
   * A list of components that will be rendered either horizontally or vertically
   */
  children: _propTypes.default.arrayOf(_propTypes.default.node).isRequired,
  /**
   * A required accessibility label that needs to be passed to be used on List
   * which is applied as normal for a React Native accessibilityLabel prop.
   */
  accessibilityLabel: _propTypes.default.string.isRequired,
  /**
   * Sets the HTML tag of the outer container and the children. By default `'li'` for the children
   * and `'ul'` for the container
   *
   * If either `tag` or `childrenTag` is overridden, the other should be too, to avoid producing invalid HTML.
   *
   */
  tag: _propTypes.default.oneOf(_utils.layoutTags),
  childrenTag: _propTypes.default.oneOf(_utils.layoutTags)
};
var _default = exports.default = Timeline;